import { t } from "@lingui/macro";
import { getCyclingLegendItems } from "../Shared/legendItems";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNativeMessagingContext, useDrawerContext } from "@WahooFitness/wahoo-offline-mfe";
import { useTheme } from "@mui/material";
import { useNavigate } from "react-router";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import useLocalizedCyclingFitnessData from "./useLocalizedCyclingFitnessData";
import { FitnessProfileAlgorithm, WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import HowWeCalculateDrawer from "../HowWeCalculateDrawer";
import { ClickTarget, EventType, SourcePage } from "@/services/Analytics/EventTypes";
import useDismiss from "@/hooks/useDismiss";
import useRiderType from "../../RiderType/useRiderType";
import EstimateDrawer from "../EstimateDrawer";
import { TrendIcon } from "@WahooFitness/redesignr";
import useUserProfiles from "@/hooks/useUserProfiles";
import useFitnessProfiles from "@/hooks/useFitnessProfiles";
import EditProfileDrawer from "../EditProfileDrawer";
import AthleteProfileLogo from "../AthleteProfileLogo";
import useLocalStorage from "@/hooks/useLocalStorage";
import useFitnessAssessmentClickHandler from "@/hooks/useFitnessAssessmentClickHandler";

const useCyclingFitnessProfile = () => {
  const { addRefreshListener, removeRefreshListener } = useNativeMessagingContext();
  const { logEvent } = useLogEvent();
  const [editProfileDrawerOpen, setEditProfileDrawerOpen] = useState(false);

  const { setDrawer } = useDrawerContext();
  const navigate = useNavigate();
  const { data: hasSeenFourDPEducationFlow } = useLocalStorage("hasSeenFourDPEducationFlow");

  const { indoorRiderTypeId: systmRiderTypeId } = useRiderType();
  const { getRiderTypeById, getRiderTypeStrengthById, getRiderTypeWeaknessById } =
    useLocalizedCyclingFitnessData();

  const { data: userDismissedRiderTypeAlert, dismiss: dismissRiderTypeAlert } = useDismiss(
    "userDismissedRiderTypeAlert"
  );

  const {
    userFitnessProfilesData,
    userFitnessProfilesIsLoading,
    userFitnessProfilesError,
    mutateUserFitnessProfile,
  } = useFitnessProfiles();

  const fitnessProfileRef = useRef<any>(null);

  useEffect(() => {
    const refreshListener = addRefreshListener(mutateUserFitnessProfile);
    return () => removeRefreshListener(refreshListener);
  }, [addRefreshListener, mutateUserFitnessProfile, removeRefreshListener]);

  const logFitnessProfileEvent = useCallback(
    (
      clickTarget: ClickTarget,
      eventType = EventType.click,
      sourcePageName = SourcePage.fitness_profile
    ) => {
      logEvent("athlete_profile", {
        event_type: eventType,
        click_target: clickTarget,
        source_page_name: sourcePageName,
      });
    },
    [logEvent]
  );

  const handleDismissRiderTypeAlert = useCallback(() => {
    logFitnessProfileEvent(
      ClickTarget.rider_type_mismatch_alert,
      EventType.dismiss,
      SourcePage.athlete_profile_cycling
    );
    dismissRiderTypeAlert();
  }, [dismissRiderTypeAlert, logFitnessProfileEvent]);

  const { userProfilesData } = useUserProfiles();

  const hasCyclingTrainingPreference = useMemo(
    () =>
      userProfilesData?.workout_type_family_ids.length === 0 ||
      userProfilesData?.workout_type_family_ids.includes(WahooWorkoutTypeFamily.Cycling),
    [userProfilesData?.workout_type_family_ids]
  );

  const cyclingFitnessProfilesData = useMemo(
    () =>
      userFitnessProfilesData?.find(
        (profile) => profile.workout_type_family_id === WahooWorkoutTypeFamily.Cycling
      ),
    [userFitnessProfilesData]
  );

  const riderTypeData = useMemo(
    () => getRiderTypeById(cyclingFitnessProfilesData?.fitness_type_id || undefined),
    [cyclingFitnessProfilesData?.fitness_type_id, getRiderTypeById]
  );

  const riderTypeStrength = useMemo(
    () => getRiderTypeStrengthById(cyclingFitnessProfilesData?.fitness_strength_id || undefined),
    [cyclingFitnessProfilesData?.fitness_strength_id, getRiderTypeStrengthById]
  );
  const riderTypeWeakness = useMemo(
    () => getRiderTypeWeaknessById(cyclingFitnessProfilesData?.fitness_weakness_id || undefined),
    [cyclingFitnessProfilesData?.fitness_weakness_id, getRiderTypeWeaknessById]
  );
  const riderStrength = useMemo(() => {
    if (riderTypeStrength) {
      return {
        name: riderTypeStrength?.name,
        description: riderTypeStrength?.description,
        icon: <TrendIcon size="small" direction="up" color="success" />,
      };
    }
  }, [riderTypeStrength]);
  const riderWeakness = useMemo(() => {
    if (riderTypeWeakness) {
      return {
        name: riderTypeWeakness?.name,
        description: riderTypeWeakness?.description,
        icon: <TrendIcon size="small" direction="down" color="error" />,
      };
    }
  }, [riderTypeWeakness]);

  const riderTypeIcon = useMemo(() => riderTypeData?.icon, [riderTypeData?.icon]);
  const riderTypeIconFilled = useMemo(() => riderTypeData?.iconFilled, [riderTypeData?.iconFilled]);
  const riderTypeName = useMemo(() => riderTypeData?.name, [riderTypeData?.name]);

  const { palette } = useTheme();

  const hasProfile = !!cyclingFitnessProfilesData;

  const dimensions = useMemo(() => {
    return {
      ftp: {
        value: cyclingFitnessProfilesData?.dim_1 ?? 0,
        rank: cyclingFitnessProfilesData?.dim_1_rank ?? 0,
      },
      map: {
        value: cyclingFitnessProfilesData?.dim_2 ?? 0,
        rank: cyclingFitnessProfilesData?.dim_2_rank ?? 0,
      },
      ac: {
        value: cyclingFitnessProfilesData?.dim_3 ?? 0,
        rank: cyclingFitnessProfilesData?.dim_3_rank ?? 0,
      },
      nm: {
        value: cyclingFitnessProfilesData?.dim_4 ?? 0,
        rank: cyclingFitnessProfilesData?.dim_4_rank ?? 0,
      },
    };
  }, [
    cyclingFitnessProfilesData?.dim_1,
    cyclingFitnessProfilesData?.dim_1_rank,
    cyclingFitnessProfilesData?.dim_2,
    cyclingFitnessProfilesData?.dim_2_rank,
    cyclingFitnessProfilesData?.dim_3,
    cyclingFitnessProfilesData?.dim_3_rank,
    cyclingFitnessProfilesData?.dim_4,
    cyclingFitnessProfilesData?.dim_4_rank,
  ]);

  const legendItems = getCyclingLegendItems(palette);

  const profileDescription = useMemo(() => {
    if (riderTypeData?.description) return riderTypeData.description;
    if (!cyclingFitnessProfilesData?.fitness_type_id) {
      return t`Your Athlete Profile and rider type give you a complete view of your strengths and weaknesses, 
      revealing what you are capable of now and giving you insights into how you’ll perform in 
      different conditions.`;
    }
    return "";
  }, [cyclingFitnessProfilesData?.fitness_type_id, riderTypeData?.description]);

  const handleQuestionnaireClick = useCallback(() => {
    setDrawer({ open: false });
    logFitnessProfileEvent(ClickTarget.start_quiz);
    hasSeenFourDPEducationFlow ? navigate("/fourdp?skipEducation") : navigate("/fourdp");
  }, [hasSeenFourDPEducationFlow, logFitnessProfileEvent, navigate, setDrawer]);

  const openInfoDrawer = useCallback(() => {
    logFitnessProfileEvent(ClickTarget.how_we_calculate);
    setDrawer({
      open: true,
      title: t`How we calculate?`,
      hidePuller: true,
      children: (
        <HowWeCalculateDrawer
          infoText={t`This chart visualizes your Athlete Profile and compares how each metric ranks to one another for your specific demographic. It factors in your height, weight, age, sex and fitness level to determine your abilities. If a value falls in the modest range, it highlights areas for improvement. If the value falls in the exceptional range, it highlights your strengths as a rider.`}
        />
      ),
      elevation: 4,
    });
  }, [setDrawer, logFitnessProfileEvent]);

  const isRiderTypeEstimate = useMemo(
    () =>
      cyclingFitnessProfilesData?.fitness_profile_algorithm_id &&
      [
        FitnessProfileAlgorithm.FourDPQuiz,
        FitnessProfileAlgorithm.HalfMontyAssessment,
        FitnessProfileAlgorithm.SystmOnboarding,
      ].includes(cyclingFitnessProfilesData.fitness_profile_algorithm_id),
    [cyclingFitnessProfilesData?.fitness_profile_algorithm_id]
  );

  const isRiderTypeManual = useMemo(
    () =>
      cyclingFitnessProfilesData?.fitness_profile_algorithm_id === FitnessProfileAlgorithm.Custom,
    [cyclingFitnessProfilesData?.fitness_profile_algorithm_id]
  );

  const openEstimateExplanationDrawer = useCallback(() => {
    logFitnessProfileEvent(isRiderTypeManual ? ClickTarget.manual_chip : ClickTarget.estimate_chip);
    setDrawer({
      open: true,
      hidePuller: true,
      title: isRiderTypeManual ? t`About manual tag` : t`Why is this an estimate?`,
      children: (
        <EstimateDrawer algorithmId={cyclingFitnessProfilesData?.fitness_profile_algorithm_id} />
      ),
    });
  }, [
    cyclingFitnessProfilesData?.fitness_profile_algorithm_id,
    isRiderTypeManual,
    logFitnessProfileEvent,
    setDrawer,
  ]);

  const hasRiderTypeMismatch = useMemo(
    // right now the IDs match 1:1 this shouldn't change, but it could.
    () => systmRiderTypeId && cyclingFitnessProfilesData?.fitness_type_id !== systmRiderTypeId,
    [cyclingFitnessProfilesData?.fitness_type_id, systmRiderTypeId]
  );

  const showEditProfile = useMemo(() => {
    return !!cyclingFitnessProfilesData;
  }, [cyclingFitnessProfilesData]);

  const handleEditProfileClick = useCallback(() => {
    logEvent("4dp_manual_update", {
      event_type: EventType.click,
      click_target: ClickTarget.update_manually,
      source_page_name: SourcePage.fitness_profile,
    });
    setDrawer({ open: false });
    setEditProfileDrawerOpen(true);
  }, [logEvent, setDrawer]);

  const handleEditDrawerClose = useCallback(() => {
    setEditProfileDrawerOpen(false);
    setTimeout(() => {
      fitnessProfileRef?.current?.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, 1);
  }, []);

  const handleOpenHistoryDrawer = useCallback(() => {
    logFitnessProfileEvent(ClickTarget.view_historical_fitness_profiles);
    navigate("/historical-fitness-profiles");
  }, [logFitnessProfileEvent, navigate]);

  const { handleFitnessTestClick } = useFitnessAssessmentClickHandler(SourcePage.fitness_profile);

  const handleViewWorkout = useCallback(() => {
    setDrawer({ open: false });
    handleFitnessTestClick();
  }, [handleFitnessTestClick, setDrawer]);

  const openUpdateProfileDrawer = useCallback(() => {
    setDrawer({
      open: true,
      title: hasProfile ? t`Update profile` : t`Start profiling`,
      hidePuller: true,
      children: (
        <EditProfileDrawer
          onStartQuestionnaire={handleQuestionnaireClick}
          onStartFitnessAssessment={handleViewWorkout}
          showFitnessAssessmentButton={true}
          questionnaireIcon={<AthleteProfileLogo profileType={WahooWorkoutTypeFamily.Cycling} />}
          showManualUpdateButton={hasProfile}
          onManualUpdateClick={handleEditProfileClick}
        />
      ),
      elevation: 4,
    });
  }, [setDrawer, hasProfile, handleQuestionnaireClick, handleViewWorkout, handleEditProfileClick]);

  return {
    userFitnessProfilesIsLoading,
    userFitnessProfilesError,
    mutateFitnessProfile: mutateUserFitnessProfile,
    cyclingFitnessProfilesData,
    hasProfile,
    dimensions,
    legendItems,
    riderTypeName,
    riderTypeIcon,
    riderTypeIconFilled,
    profileDescription,
    userFitnessProfilesData,
    handleQuestionnaireClick,
    riderStrength,
    riderWeakness,
    hasCyclingTrainingPreference,
    openInfoDrawer,
    isRiderTypeEstimate,
    isRiderTypeManual,
    userDismissedRiderTypeAlert,
    handleDismissRiderTypeAlert,
    hasRiderTypeMismatch,
    openEstimateExplanationDrawer,
    editProfileDrawerOpen,
    handleEditProfileClick,
    handleEditDrawerClose,
    showEditProfile,
    fitnessProfileRef,
    handleOpenHistoryDrawer,
    openUpdateProfileDrawer,
  };
};

export default useCyclingFitnessProfile;
